import { eventQueries } from "@/components/calendars/api.ts";
import {
  useEventFromCache,
  useEventPanelTimeKey,
} from "@/components/calendars/eventDetailHooks.ts";
import { isReminderEvent } from "@/components/calendars/canEditEvent.ts";
import { claimEventProposal } from "@/components/calendars/pendingEventProposal.ts";
import { eventPendingDeleteAtom } from "@/components/calendars/eventDeleteAtom.ts";
import {
  type EventDetailCloseGuard,
  EventDetailPanel,
} from "@/components/calendars/EventDetailPanel.tsx";
import { ReminderDetailPanel } from "@/components/calendars/ReminderDetailPanel.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { useQuery } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useCallback, useRef, useState } from "react";

/**
 * An event's detail, with no chrome of its own — the host supplies that.
 *
 * Chrome-free and shared for the same reason `ContactDetailBody` is: an event is
 * shown on more than one surface, and a second hand-built layout would drift
 * from this one on recurrence, RSVP scope, conferencing, and delete/cancel.
 * Today the host is the calendar page, which renders this full-screen on mobile
 * where there is no canvas to card into and no room to anchor a popover.
 *
 * `onClose` is how the host is told the user is done — it owns the address, so
 * closing is its navigation to make, not this component's.
 */
export const EventDetailBody = ({ eventId, onClose }: { eventId: string; onClose: () => void }) => {
  const setEventPendingDelete = useSetAtom(eventPendingDeleteAtom);
  // Published by the panel; routes a dismissal through its unsaved-changes
  // prompt, the same way the popover and canvas card do.
  const closeGuardRef = useRef<EventDetailCloseGuard | null>(null);
  const [resetKey, setResetKey] = useState(0);
  // Claimed once, at mount: where there is no canvas to card into, a drag's
  // "Continue editing" lands here instead (see pendingEventProposal).
  const [proposal] = useState(() => claimEventProposal(eventId));
  const cachedEvent = useEventFromCache(eventId);
  const { data: fetchedEvent, isLoading } = useQuery({
    ...eventQueries.detail(eventId),
    enabled: !!eventId && !cachedEvent,
  });
  // Prefer the live cache lookup: socket-driven refetches keep it fresh, while
  // the detail query stops refetching once `enabled` flips false. A successful
  // save writes both copies, so the fallback serves the cache-miss (deep-link)
  // case.
  const event = cachedEvent ?? fetchedEvent;

  // Pristine → remount on an external reschedule; dirty → key frozen so the
  // draft survives (see useEventPanelTimeKey).
  const [isDirty, setIsDirty] = useState(false);
  const timeKey = useEventPanelTimeKey(event, isDirty);

  // Clear dirty in the same batch as the remount: waiting for the fresh
  // panel's onDirtyChange(false) would unfreeze timeKey a render later and
  // trigger a second remount when the time changed during the edit.
  const handleCancel = useCallback(() => {
    setIsDirty(false);
    setResetKey((k) => k + 1);
  }, []);

  if (isLoading && !event) {
    return <Loader className="py-8" />;
  }
  if (!event) {
    return <p className="text-muted-foreground text-sm p-4">Event not found.</p>;
  }
  // A link can land on a reminder as easily as a meeting, and a reminder gets
  // its own much smaller panel — see ReminderDetailPanel.
  if (isReminderEvent(event)) {
    return (
      <ReminderDetailPanel
        key={`${event.gcalId}-${resetKey}-${timeKey}`}
        event={event}
        onClose={onClose}
      />
    );
  }
  return (
    <EventDetailPanel
      key={`${event.gcalId}-${resetKey}-${timeKey}`}
      event={event}
      prospectiveStart={proposal?.startIso}
      prospectiveEnd={proposal?.endIso}
      onClose={onClose}
      onCancel={handleCancel}
      onDirtyChange={setIsDirty}
      closeGuardRef={closeGuardRef}
      // This surface draws the panel's own header, so its Delete arrives here
      // rather than from a host button. Without this the button rendered and
      // did nothing. Guarded like every other host: closing on a dirty form
      // would discard the edit, and cancelling the confirmation would leave
      // nothing to go back to.
      onRequestDelete={() => {
        const proceed = () => {
          setEventPendingDelete(event);
          onClose();
        };
        if (closeGuardRef.current) closeGuardRef.current(proceed);
        else proceed();
      }}
      hideClose
    />
  );
};
